import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
  PROVIDER_SEARCH,
  PROVIDER_SEARCH_MORE,
  providerSearch,
  reset as searchStateReset,
  setFilterOptions
} from 'store/providerSearch/actions';
import {
  searchStateSelector,
  searchTermsAndConditionsAccepted,
  searchFilterOptionsSelector
} from 'store/providerSearch/selectors';
import { isDfdAuthenticatedSelector } from 'store/authentication/selectors';
import { isSelectHealthMemberSelector, profileInsurancesSelector } from 'store/profile/selectors';
import { ProviderSearchOptions, ProviderSummary } from 'store/providerSearch/types';
import { sortBy, searchLimit } from 'store/providerSearch/constants';
import { useProviderSearchQuickLinks } from 'lib/hooks/ProviderSearch/useProviderSearchQuickLinks';
import { Nullable } from 'modules/types/common';
import { useProviderSearchSuggestions } from './useProviderSearchSuggestions';
import { QuickLink, Suggestion } from './types';
import { insuranceAliasValues, filters } from './Filters';

//
//  Hook: useProviderSearch
//
//  Description:
//  Acts as single source of truth for anything related to provider search
//
//  Props:
//  initialQuery?: string;
//  initialOptions?: Partial<ProviderSearchOptions>
//

export interface ProviderSearchInterface {
  query: string;
  result: ProviderSummary[];
  error: Error;
  suggestions: Nullable<Suggestion[]>;
  quickLinks: QuickLink[];
  options: ProviderSearchOptions;
  fetching: boolean;
  searched: boolean;
  reset: (options?: Partial<ProviderSearchOptions>) => void;
  change: (newQuery: string) => void;
  search: (options?: Partial<ProviderSearchOptions>, isRecentSearch?: boolean) => void;
}

//
//  Constant: defaultOptions
//  Description: The default options used for the search
//
export const defaultOptions: Required<ProviderSearchOptions> = {
  input: '',
  page: 0,
  limit: searchLimit,
  orderBy: sortBy.LAST_NAME.value,
  locationText: '',
  filters: [],
  facets: []
};

const debouncer = debounce(cb => cb(), 200);

export function useProviderSearch(
  isMobile = false,
  initialQuery = '',
  initialOptions: Partial<ProviderSearchOptions> = {}
): ProviderSearchInterface {
  const dispatch = useDispatch();
  const persistedOptions = useSelector(searchFilterOptionsSelector);
  const searchState = useSelector(searchStateSelector);
  const termsAccepted = useSelector(searchTermsAndConditionsAccepted);
  const isSH = useSelector(isSelectHealthMemberSelector);
  const isDFD = useSelector(isDfdAuthenticatedSelector);
  const [insurance] = useSelector(profileInsurancesSelector);

  const quickLinks = useProviderSearchQuickLinks(isMobile);
  const suggestions = useProviderSearchSuggestions();

  const [query, setQuery] = useState(initialQuery);

  const options = useRef({
    ...defaultOptions,
    orderBy: termsAccepted ? sortBy.BEST_MATCH.value : sortBy.LAST_NAME.value,
    facets: isSH && insurance ? [insuranceAliasValues(insurance)] : [],
    filters: !isSH && isDFD ? [filters.organizationAffiliations.values.isMedicalGroup] : [],
    ...initialOptions
  });

  const searched = useRef(false);

  //
  //  Method: reset
  //  Description: Resets the search to its initial values
  //
  const reset = (opts?: Partial<ProviderSearchOptions>) => {
    dispatch(searchStateReset());
    suggestions.init();
    quickLinks.init();
    if (opts) {
      options.current = { ...options.current, ...opts };
    }
  };

  //
  //  Method: change
  //  Description: Changes the query and starts fetching suggestions
  //
  const change = (newQuery: string) => {
    setQuery(newQuery);

    if (!newQuery) {
      quickLinks.init();
    } else {
      debouncer(() => {
        suggestions.get(newQuery);
      });
    }
  };

  //
  //  Method: search
  //  Description: dispatches providerSearch action based on the query and options selected
  //
  const search = (opts?: Partial<ProviderSearchOptions>, isRecentSearch = false) => {
    const newOptions = { ...options.current, ...opts };

    let currentQuery = query;
    let actionType = PROVIDER_SEARCH;

    // Quick link will store query as an option.input - so uses this to set the current query
    if (isRecentSearch && newOptions.input) {
      setQuery(newOptions.input);
      currentQuery = newOptions.input;
    }

    if (typeof newOptions.page === 'number' && newOptions.page > 0) {
      actionType = PROVIDER_SEARCH_MORE;
    }
    // Clears out the suggestions and quick links when search has been called, debouce to avoid double searches
    debouncer(() => {
      suggestions.init();
    });
    quickLinks.empty();

    dispatch(providerSearch({ input: currentQuery, options: newOptions, actionType }));
    options.current = newOptions;
    dispatch(setFilterOptions(newOptions));
    searched.current = true;
  };

  return {
    query,
    options: persistedOptions,
    searched: searched.current,
    quickLinks: quickLinks.data,
    suggestions: suggestions.data,
    fetching: searchState.isFetching,
    error: searchState.error,
    result: searchState.data,
    reset,
    change,
    search
  } as const;
}
